import axiosInstance from '../axios';
import {
  PaginatedMessagesResponse,
  SendMessagePayload,
  SendMessageResponse,
  SendBroadcastPayload,
  BroadcastResponse,
  UnreadCountResponse,
  MarkAsReadResponse,
  CreateConcernPayload,
  Concern,
  GroupListResponse,
  GroupMessageListResponse,
  CreateGroupPayload,
  SendGroupMessagePayload,
  Message,
} from './messaging.interfaces';

const API_BASE = process.env.REACT_APP_BASE_URL || 'http://localhost:4001';
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';
const BASE_PATH = `${API_BASE}/${API_VERSION}/messages`;
const BROADCAST_PATH = `${API_BASE}/${API_VERSION}/broadcast`;
const GROUPS_PATH = `${API_BASE}/${API_VERSION}/api/messaging/groups`;

/**
 * Messaging REST API Service
 * Provides fallback and reliable message retrieval when WebSocket is unavailable
 */
export class MessagingAPI {
  /**
   * Get all conversations for the authenticated user
   */
  static async getConversations(): Promise<any[]> {
    const response = await axiosInstance.get<any>(
      `${BASE_PATH}/conversations`
    );
    const data = response.data;
    // Backend returns { conversations: [...], total: N }
    const list: any[] = data?.conversations ?? (Array.isArray(data) ? data : []);
    // Map backend camelCase shape to frontend ConversationSummary snake_case shape
    return list.map((c: any) => ({
      conversation_id: c.otherUser?._id,
      type: 'direct_message',
      other_user: c.otherUser
        ? {
          _id: c.otherUser._id,
          first_name: c.otherUser.firstName,
          last_name: c.otherUser.lastName,
          email: c.otherUser.email,
        }
        : undefined,
      last_message: c.lastMessage
        ? {
          _id: c.lastMessage._id,
          content: c.lastMessage.content,
          created_at: c.lastMessage.createdAt,
          is_read: c.lastMessage.isRead,
          sender_id: '',
          receiver_id: '',
          type: 'direct_message',
          institute_ref: '',
        }
        : undefined,
      unread_count: c.unreadCount ?? 0,
      last_activity: c.lastMessage?.createdAt,
    }));
  }

  /**
   * Get messages in a conversation with another user
   * @param otherUserId - The ID of the other user
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 50, max: 100)
   */
  static async getConversationMessages(
    otherUserId: string,
    page: number = 1,
    limit: number = 50
  ): Promise<PaginatedMessagesResponse> {
    const response = await axiosInstance.get<any>(
      `${BASE_PATH}/conversations/${otherUserId}`,
      { params: { page, limit } }
    );
    const data = response.data;
    const rawMessages: any[] = data?.messages ?? [];
    return {
      messages: rawMessages.map((m: any) => ({
        id: m._id,
        _id: m._id,
        content: m.content,
        sender_id: m.sender?._id ?? m.sender_id ?? '',
        receiver_id: m.receiver?._id ?? m.receiver_id ?? '',
        type: m.type ?? 'direct_message',
        institute_ref: m.institute_ref ?? '',
        is_read: m.isRead ?? m.is_read ?? false,
        created_at: m.createdAt ?? m.created_at,
        sender: m.sender ? {
          _id: m.sender._id,
          first_name: m.sender.firstName,
          last_name: m.sender.lastName,
          email: m.sender.email,
        } : undefined,
        receiver: m.receiver ? {
          _id: m.receiver._id,
          first_name: m.receiver.firstName,
          last_name: m.receiver.lastName,
          email: m.receiver.email,
        } : undefined,
      })),
      total: data?.total ?? 0,
      page: data?.page ?? page,
      limit: data?.limit ?? limit,
    };
  }

  /**
   * Send a direct message via REST API
   * @param payload - Message payload containing receiver_id, content, and institute_ref
   */
  static async sendMessage(
    payload: SendMessagePayload
  ): Promise<SendMessageResponse> {
    const { sender_id, ...rest } = payload;
    const body = {
      ...rest,
      created_by: sender_id,
      type: rest.type || 'direct_message',
    };
    const response = await axiosInstance.post<any>(
      `${BASE_PATH}/send`,
      body
    );
    const data = response.data;
    // Backend returns flat message object; wrap it to match SendMessageResponse
    if (data && data._id && !data.message) {
      return { status: 'success', message: data };
    }
    return data;
  }

  /**
   * Mark a single message as read
   * @param messageId - The ID of the message to mark as read
   */
  static async markMessageAsRead(messageId: string): Promise<void> {
    await axiosInstance.put(`${BASE_PATH}/${messageId}/read`);
  }

  /**
   * Mark all messages in a conversation as read
   * @param otherUserId - The ID of the other user in the conversation
   */
  static async markConversationAsRead(
    otherUserId: string
  ): Promise<MarkAsReadResponse> {
    const response = await axiosInstance.put<MarkAsReadResponse>(
      `${BASE_PATH}/conversations/${otherUserId}/mark-read`
    );
    return response.data;
  }

  /**
   * Get the total count of unread direct messages
   */
  static async getUnreadCount(): Promise<number> {
    const response = await axiosInstance.get<UnreadCountResponse>(
      `${BASE_PATH}/unread-count`
    );
    return response.data.unreadCount;
  }

  /**
   * Get broadcast messages for the authenticated user
   * @param instituteId - Institute UUID (required)
   * @param classId - Optional class UUID filter
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 50)
   */
  static async getBroadcasts(
    instituteId: string,
    classId?: string,
    page: number = 1,
    limit: number = 50
  ): Promise<PaginatedMessagesResponse> {
    const params: Record<string, string | number> = {
      instituteId,
      page,
      limit,
    };

    if (classId) {
      params.classId = classId;
    }

    const response = await axiosInstance.get<PaginatedMessagesResponse>(
      `${BASE_PATH}/broadcasts`,
      { params }
    );
    return response.data;
  }

  /**
   * Send a broadcast message (Staff only)
   * @param payload - Broadcast payload with message and target scope
   */
  static async sendBroadcast(
    payload: SendBroadcastPayload
  ): Promise<BroadcastResponse> {
    const response = await axiosInstance.post<BroadcastResponse>(
      BROADCAST_PATH,
      payload
    );
    return response.data;
  }

  /**
   * Get concerns list (filtered by type CONCERN)
   */
  static async getConcerns(): Promise<Concern[]> {
    const response = await axiosInstance.get<Concern[]>(
      `${BASE_PATH}/conversations`,
      { params: { type: 'concern' } }
    );
    return response.data as Concern[];
  }

  /**
   * Create a new concern
   */
  static async createConcern(
    payload: CreateConcernPayload
  ): Promise<SendMessageResponse> {
    const response = await axiosInstance.post<SendMessageResponse>(
      `${BASE_PATH}/send`,
      { ...payload, type: 'concern' }
    );
    return response.data;
  }

  /**
   * Get concern thread messages
   */
  static async getConcernThread(
    concernId: string,
    page = 1,
    limit = 50
  ): Promise<PaginatedMessagesResponse> {
    const response = await axiosInstance.get<PaginatedMessagesResponse>(
      `${BASE_PATH}/conversations/${concernId}`,
      { params: { page, limit } }
    );
    return response.data;
  }

  // ─── Group Chat ────────────────────────────────────────────────

  static async getGroups(
    page = 1,
    limit = 20
  ): Promise<GroupListResponse> {
    const response = await axiosInstance.get<GroupListResponse>(
      GROUPS_PATH,
      { params: { page, limit } }
    );
    return response.data;
  }

  static async getGroupMessages(
    groupId: string,
    page = 1,
    limit = 50
  ): Promise<GroupMessageListResponse> {
    const response = await axiosInstance.get<GroupMessageListResponse>(
      `${GROUPS_PATH}/${groupId}/messages`,
      { params: { page, limit } }
    );
    return response.data;
  }

  static async sendGroupMessage(
    payload: SendGroupMessagePayload
  ): Promise<Message> {
    const response = await axiosInstance.post<Message>(
      `${GROUPS_PATH}/${payload.group_id}/messages`,
      { content: payload.content }
    );
    return response.data;
  }

  static async createGroup(
    payload: CreateGroupPayload
  ): Promise<{ group: any; members: any[] }> {
    const response = await axiosInstance.post<{ group: any; members: any[] }>(
      GROUPS_PATH,
      payload
    );
    return response.data;
  }
}
